import { FC } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Stack, Typography } from '@mui/material';

import { PaymentMethodType } from '@mopla/data-models';
import {
	CreditCardIcon,
	DTicketPageLayout,
	MplButton,
	MplRadioButton,
	SepaIcon,
	StyledDTicketPageLayoutFooter,
} from '@mopla/ui';

interface IEditPaymentMethodSelectionProps {
	onBack: () => void;
	onSubmit: (paymentMethod: PaymentMethodType) => void;
}

interface IFormValues {
	paymentMethod: PaymentMethodType;
}

export const EditPaymentMethodSelection: FC<
	IEditPaymentMethodSelectionProps
> = ({ onBack, onSubmit }) => {
	const { t } = useTranslation('editPaymentMethod');

	const { control, handleSubmit, watch } = useForm<IFormValues>();

	const selectedMethod = watch('paymentMethod');

	const onFormSubmit = (data: IFormValues) => {
		onSubmit(data.paymentMethod);
	};

	return (
		<DTicketPageLayout
			title={t('change_title')}
			onBack={onBack}
			scrollable={true}
			dataTestId="payment-method-selection-page"
		>
			<Stack gap={1.5}>
				<Typography variant="h4-sb" color="primary.main">
					{t('select_prompt')}
				</Typography>

				<Controller
					name="paymentMethod"
					control={control}
					render={({ field }) => (
						<Stack gap={2}>
							<MplRadioButton
								text={t('sepa_option')}
								icon={<SepaIcon />}
								checked={field.value === 'sepa'}
								onChange={() => field.onChange('sepa')}
								value="sepa"
								data-testid="PaymentMethodSelection-sepaOption"
							/>
							<MplRadioButton
								text={t('other_option')}
								icon={<CreditCardIcon />}
								checked={field.value === 'other'}
								onChange={() => field.onChange('other')}
								value="other"
								data-testid="PaymentMethodSelection-otherOption"
							/>
						</Stack>
					)}
				/>
			</Stack>

			<StyledDTicketPageLayoutFooter>
				<MplButton
					testId="PaymentMethodSelection-submitButton"
					fullWidth
					disabled={!selectedMethod}
					onClick={handleSubmit(onFormSubmit)}
				>
					{t('submit_button')}
				</MplButton>
			</StyledDTicketPageLayoutFooter>
		</DTicketPageLayout>
	);
};
